/**
 * Smart Scroll
 * Two-step scroll animation: jump near target, then animate the final distance
 * Use data-smartscroll attribute on anchor elements linking to page anchors
 * Optional: data-smartscroll-offset="100" to set custom offset
 */

class SmartScroll {
  constructor(options = {}) {
    this.jumpDistance = options.jumpDistance || 400;
    this.duration = options.duration || 300;
    this.defaultOffset = options.offset || 35;
    
    this.init();
  }

  /**
   * Check if current scroll position is above the target element
   */
  isScrollAboveElement(element) {
    const rect = element.getBoundingClientRect();
    return rect.top > 0;
  }

  /**
   * Animate scroll to a position
   */
  animateScrollTo(targetY, duration) {
    return new Promise(resolve => {
      const startY = window.scrollY;
      const diff = targetY - startY;
      const startTime = performance.now();

      if (duration === 0) {
        window.scrollTo(0, targetY);
        resolve();
        return;
      }

      const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        
        window.scrollTo(0, startY + diff * eased);
        
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(step);
    });
  }

  /**
   * Smart scroll: jump near element, then animate the final distance
   */
  scrollTo(element, offset = this.defaultOffset) {
    const rect = element.getBoundingClientRect();
    const elementY = window.scrollY + rect.top - offset;
    const jumpDistance = this.isScrollAboveElement(element) ? -this.jumpDistance : this.jumpDistance;
    const jumpY = elementY + jumpDistance;

    // First: instant jump to position near element
    this.animateScrollTo(jumpY, 0).then(() => {
      // Then: smooth scroll the final distance
      this.animateScrollTo(elementY, this.duration);
    });
  }

  /**
   * Initialize delegated event listener
   */
  init() {
    document.addEventListener('click', (e) => {
      // Find closest anchor with data-smartscroll attribute
      const link = e.target.closest('a[data-smartscroll]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;

      const targetId = href.slice(1);
      const target = document.getElementById(targetId);
      if (!target) return;

      e.preventDefault();
      
      // Get optional offset from data attribute
      const offset = parseInt(link.dataset.smartscrollOffset, 10) || this.defaultOffset;
      
      this.scrollTo(target, offset);
      history.pushState(null, '', href);
    });
  }
}

// Initialize on DOM ready
let smartScrollInstance;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    smartScrollInstance = new SmartScroll();
  });
} else {
  smartScrollInstance = new SmartScroll();
}

export default SmartScroll;

